<script lang="ts">
export default {
  name: 'PolicyOverrideTargetSummary',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { CheckCircle2, FileText } from '@lucide/vue';
import type { ExtractionTarget, ReviewSummary } from '@/types/ai';

// What reviewing a binder that is already attached to a policy commits to. Read-only:
// unlike the external panel there is nothing to create or discard here -- finishing the
// review in the field list beside this is what applies the values.
const props = withDefaults(
  defineProps<{
    target: ExtractionTarget;
    reviews?: ReviewSummary[];
  }>(),
  { reviews: () => [] },
);

const accountName = computed(() => props.target.accountName || 'this account');

const headline = computed(() =>
  props.target.policyNumber
    ? `Updates policy ${props.target.policyNumber} on ${accountName.value}`
    : `Updates an existing policy on ${accountName.value}`,
);

const lastReview = computed(() => props.reviews[0] ?? null);

const reviewedOn = computed(() => {
  const reviewedAt = lastReview.value?.reviewedAt;
  if (!reviewedAt) {
    return '';
  }
  const parsed = new Date(reviewedAt);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString();
});
</script>

<template>
  <div
    class="shrink-0 rounded-lg border border-slate-200 bg-white/80 p-3 backdrop-blur-sm"
    data-testid="policy-override-target-summary">
    <p class="flex items-center gap-1.5 text-sm font-medium">
      <FileText class="h-4 w-4 shrink-0 text-slate-500" />
      {{ headline }}
    </p>
    <p class="mt-0.5 text-xs text-slate-600">
      Finishing the review applies the fields this policy's source allows to that policy. No policy is created.
    </p>

    <p
      v-if="lastReview"
      class="mt-2 flex items-start gap-1.5 text-xs text-slate-600"
      data-testid="prior-review">
      <CheckCircle2 class="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-700" />
      <span>
        Validated by {{ lastReview.userEmail }}<template v-if="reviewedOn"> on {{ reviewedOn }}</template
        >. Reviewing again records another review.
      </span>
    </p>
  </div>
</template>
